import { CallbackWithRecommendationsWidget } from '@cian/callback-widget';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getOfferUserId } from '../../selectors';
import { getAnalyticsUser, getOfferCustomUrl } from '../../selectors/analytics';
import {
  getCallbackRecommendations,
  getPopupCallbackIsOpen,
  getPopupCallbackOpener,
} from '../../selectors/callbackRecommendations';
import { getAgentPhone, getUserPhone } from '../../selectors/callbackRightNow';
import { isCallbackRecommendationsEnabled } from '../../selectors/isCallbackRecommendationsEnabled';
import { getNewbuildingName, selectTimezone } from '../../selectors/newObject';
import { getWorkTimeSchedule } from '../../services/workTime';
import { toggle } from '../../store/popups/callback';
import { trackRecommendationsToggleOff, trackRecommendationsToggleOn } from '../../tracking/callback';
import { TCallbackOpener } from '../../types/callbackPopup';
import { useApplicationContext } from '../ApplicationContext';
import {
  getPrivacyPolicyUrl,
  getSmsDisclaimerState,
  phoneToString,
  requestCallbackWithRecommendations,
  TRequestCallbackParameters,
} from './utils';

export const CallbackWidgetContainer = () => {
  const { config, httpApi, logger } = useApplicationContext();

  const agentPhone = useSelector(getAgentPhone);
  const timezone = useSelector(selectTimezone);
  const user = useSelector(getAnalyticsUser);
  const userPhone = useSelector(getUserPhone);
  const customUrl = useSelector(getOfferCustomUrl);
  const isOpen = useSelector(getPopupCallbackIsOpen);
  const opener = useSelector(getPopupCallbackOpener);
  const recommendations = useSelector(getCallbackRecommendations);
  const isRecommendationsEnabled = useSelector(isCallbackRecommendationsEnabled);
  const offerUserId = useSelector(getOfferUserId);
  const newbuildingName = useSelector(getNewbuildingName);
  const dispatch = useDispatch();

  const privacyPolicyUrl = getPrivacyPolicyUrl(config);
  const isSMSDisclaimerEnabled = getSmsDisclaimerState(config);

  const closePopup = React.useCallback(() => dispatch(toggle(undefined, false)), [dispatch]);

  const handleRecommendationsChange = React.useCallback((status: boolean) => {
    if (status) {
      trackRecommendationsToggleOn();
    } else {
      trackRecommendationsToggleOff();
    }
  }, []);

  const handleRequestCallback = React.useCallback(
    (options: TRequestCallbackParameters) => {
      const dependencies = { httpApi, logger };

      const meta = {
        analyticsLabel: customUrl,
        placeType: opener as TCallbackOpener,
        user,
      };

      return requestCallbackWithRecommendations(dependencies, options, meta);
    },
    [user, customUrl, opener, httpApi, logger],
  );

  const handleRequestWorkTime = React.useCallback(async () => {
    const result = await getWorkTimeSchedule({ httpApi, logger }, { userId: offerUserId as number });

    return result;
  }, [httpApi, logger, offerUserId]);

  return (
    <CallbackWithRecommendationsWidget
      agentPhone={phoneToString(agentPhone)}
      newbuildingName={newbuildingName}
      onClose={closePopup}
      onRecommendationsChange={handleRecommendationsChange}
      onRequestCallback={handleRequestCallback}
      onRequestWorkTime={handleRequestWorkTime}
      open={isOpen}
      privacyPolicyUrl={privacyPolicyUrl}
      recommendations={isRecommendationsEnabled ? recommendations : undefined}
      timezoneOffset={timezone}
      userPhoneNumber={userPhone ? userPhone.substring(1) : undefined}
      isSMSDisclaimerEnabled={isSMSDisclaimerEnabled}
    />
  );
};
